import type { DashboardBoard, LayoutEntry } from '@palaccount/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api';
import { throwApiError } from '@/lib/server-messages';

/**
 * PART 14 data layer. One query per widget CLUSTER (BR-DSH-00-13: a filter
 * change re-reads every widget at once → one request), plus the layout pair.
 * TanStack Query gives every widget the shared cache entry; `dataUpdatedAt`
 * feeds the freshness stamp.
 */

const KEY = ['dashboards'] as const;

export function useLayout(board: DashboardBoard) {
  return useQuery({
    queryKey: [...KEY, 'layout', board],
    queryFn: async (): Promise<LayoutEntry[]> => {
      const res = await api.dashboards.getLayout({ params: { board } });
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body.layout;
    },
  });
}

/**
 * Both layout writes answer with the truth and put it straight in the cache,
 * so the board never round-trips a read it already knows the answer to.
 *
 * `onMutate` cancels any outstanding LAYOUT READ first, and that is not
 * housekeeping. Without it a GET that was still in flight when the write went
 * out resolves afterwards and writes the PRE-change layout back over the
 * cache — the widget the user just added silently disappears. It only happens
 * when the read is slow enough to still be running at click time, which is
 * why it presented as an intermittent, load-sensitive failure rather than a
 * bug. (The board additionally refuses to act on a layout it has not read
 * yet; the two together close the window from both sides.)
 */
function cancelLayoutRead(client: ReturnType<typeof useQueryClient>, board: DashboardBoard) {
  return client.cancelQueries({ queryKey: [...KEY, 'layout', board] });
}

export function useSaveLayout(board: DashboardBoard) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (layout: LayoutEntry[]) => {
      const res = await api.dashboards.putLayout({ params: { board }, body: { layout } });
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body.layout;
    },
    onMutate: () => cancelLayoutRead(client, board),
    onSuccess: (layout) => client.setQueryData([...KEY, 'layout', board], layout),
  });
}

export function useResetLayout(board: DashboardBoard) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api.dashboards.resetLayout({ params: { board } });
      if (res.status !== 204) throwApiError(res.status, res.body);
    },
    onMutate: () => cancelLayoutRead(client, board),
    onSuccess: () => client.setQueryData([...KEY, 'layout', board], []),
  });
}

export function useFinancialDashboard() {
  return useQuery({
    queryKey: [...KEY, 'financial'],
    queryFn: async () => {
      const res = await api.dashboards.financial();
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body;
    },
  });
}

export function useSalesWidgets(range: { from?: string; to?: string }) {
  return useQuery({
    queryKey: [...KEY, 'sales', range],
    queryFn: async () => {
      const res = await api.dashboards.sales({ query: range });
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body;
    },
  });
}

export function useInventoryWidgets(filter: {
  warehouseId?: string | undefined;
  categoryId?: string | undefined;
  days?: number | undefined;
}) {
  return useQuery({
    queryKey: [...KEY, 'inventory', filter],
    queryFn: async () => {
      const res = await api.dashboards.inventory({ query: filter });
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body;
    },
  });
}

/**
 * BR-DSH-04-32's filter travels in the KEY as well as in the query, so
 * switching currency re-reads rather than showing the previous currency's
 * figures under the new label — BR-DSH-00-13's "one filter change re-reads
 * every widget".
 */
export function useCashflowWidgets(range: { from?: string; to?: string; currency?: string }) {
  return useQuery({
    queryKey: [...KEY, 'cashflow', range],
    queryFn: async () => {
      const res = await api.dashboards.cashflow({ query: range });
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body;
    },
  });
}

export function useCashflowForecast(query: { currency?: string } = {}) {
  return useQuery({
    queryKey: [...KEY, 'forecast', query],
    queryFn: async () => {
      const res = await api.dashboards.cashflowForecast({ query });
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body;
    },
  });
}
